import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getProductBySlug } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { Minus, Plus, Check, ArrowLeft } from "lucide-react";

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [colorIndex, setColorIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setColorIndex(0);
    setQuantity(1);
    window.scrollTo(0, 0);
  }, [slug]);

  const color = useMemo(() => product?.colors[colorIndex], [product, colorIndex]);

  if (!product) {
    return (
      <div className="page-narrow" data-testid="not-found">
        <h2>Bike not found.</h2>
        <Link to="/" className="btn btn-ghost">← Back home</Link>
      </div>
    );
  }

  const handleAdd = () => {
    addItem({
      product_id: product.id,
      name: product.name,
      price: product.price,
      color: color.name,
      image: color.image || product.image,
      filter: color.image ? undefined : color.filter,
      hex: color.hex,
      quantity,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="product-page" data-testid="product-page">
      <Link to="/#bikes" className="back-link" data-testid="back-link">
        <ArrowLeft size={14} /> BACK TO FLEET
      </Link>

      <div className="product-grid">
        <div className="product-image-col">
          <div className="product-image-card">
            <img
              src={color.image || product.image}
              alt={`${product.name} - ${color.name}`}
              style={{
                filter: color.image ? "none" : color.filter,
                transition: "filter 380ms ease, opacity 240ms ease",
              }}
              key={color.image || color.name}
              data-testid="product-image"
            />
            <div className="image-color-tag">
              <span className="dot" style={{ background: color.hex }} />
              {color.name}
            </div>
          </div>
        </div>

        <div className="product-info-col">
          <p className="eyebrow">{product.tagline}</p>
          <h1 className="product-title">{product.name}</h1>
          <p className="product-price">€{product.price}</p>

          <p className="product-desc" data-testid="product-description">
            {product.description}
          </p>

          <div className="spec-row">
            {product.specs.map((s) => (
              <div className="spec" key={s.label}>
                <span className="spec-label">{s.label}</span>
                <span className="spec-value">{s.value}</span>
              </div>
            ))}
          </div>

          <div className="config-block">
            <div className="config-head">
              <span>COLOR</span>
              <strong data-testid="selected-color">{color.name}</strong>
            </div>
            <div className="swatches" role="radiogroup" aria-label="Color">
              {product.colors.map((c, i) => (
                <button
                  key={c.name}
                  type="button"
                  role="radio"
                  aria-checked={i === colorIndex}
                  aria-label={c.name}
                  className={`swatch ${i === colorIndex ? "is-active" : ""}`}
                  style={{ background: c.hex }}
                  onClick={() => setColorIndex(i)}
                  data-testid={`color-swatch-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {i === colorIndex && <Check size={14} strokeWidth={3} />}
                </button>
              ))}
            </div>
          </div>

          <div className="config-block">
            <div className="config-head">
              <span>QUANTITY</span>
              <strong data-testid="quantity-display">{quantity}</strong>
            </div>
            <div className="qty-control">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                data-testid="qty-decrement"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span className="qty-value">{quantity}</span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(20, q + 1))}
                data-testid="qty-increment"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div className="action-row">
            <button
              className="btn btn-primary btn-lg"
              onClick={handleAdd}
              data-testid="add-to-cart-btn"
            >
              {added ? "ADDED TO CART ✓" : `ADD TO CART · €${product.price * quantity}`}
            </button>
            <button
              className="btn btn-ghost btn-lg"
              onClick={() => {
                addItem({
                  product_id: product.id,
                  name: product.name,
                  price: product.price,
                  color: color.name,
                  image: color.image || product.image,
                  filter: color.image ? undefined : color.filter,
                  hex: color.hex,
                  quantity,
                });
                const amount = product.price * quantity;
                window.location.href = `https://www.paypal.com/paypalme/${process.env.REACT_APP_PAYPAL_ME_USER}/${amount}`;
              }}
              data-testid="buy-now-btn"
            >
              BUY NOW →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
